import type { ILaunchpad } from '@tacans/shared-marketplace-layer';
import Link from 'next/link';
import React, { useEffect } from 'react';
import { generatePath, RouteE } from 'src/constants/pages.constants';
import { useCheckLaunchpadAccess } from 'src/features/common/launchpad/hooks/use-check-launchpad-access';
import { useLaunchpadAvatarAssets } from 'src/features/common/launchpad/hooks/use-launchpad-avatar-assets';
import { useLaunchpadBannerAssets } from 'src/features/common/launchpad/hooks/use-launchpad-banner-assets';
import { useLaunchpadStatus } from 'src/features/common/launchpad/hooks/use-launchpad-status';
import { useDisplayName } from 'src/features/common/user/hooks/use-display-name.hooks';
import { useFetchUserList, useUserListEntities } from 'src/features/common/user/hooks/user-list.hooks';
import { useCoins } from 'src/features/common/wallet/hooks/wallet.hooks';
import { ProtectedLink } from 'src/ui/common/atoms/protected-link';
import { Typography } from 'src/ui/common/atoms/typography';
import { LazyLoadMedia } from 'src/ui/common/organisms/lazy-load-media';
import { makePriceByNano } from 'src/utils/entity.utils';
import { decodeHTMLEntities, wipeHtml } from 'src/utils/html.utils';
import {
    StyledCreatorName,
    StyledDescription,
    StyledImgPreviewWrapper,
    StyledImgWrapper,
    StyledInfo,
    StyledName,
    StyledWrapper,
    StyledStatistics,
    StyledStatisticItem,
} from './styles';

export const LaunchpadCard: React.VFC<{ data: ILaunchpad; testId?: string }> = ({ data, testId }) => {
    const userListEntities = useUserListEntities();
    const fetchUsers = useFetchUserList();
    const avatarAssets = useLaunchpadAvatarAssets({ launchpad: data });
    const bannersAssets = useLaunchpadBannerAssets({ launchpad: data });
    const coins = useCoins();

    const link = generatePath(RouteE.LAUNCHPAD_DETAILS, {
        id: data.id,
    });

    const creator = userListEntities[data.ownerId];

    const creatorName = useDisplayName(creator);

    const creatorLink = generatePath(RouteE.PROFILE, {
        login: creator?.login ?? '',
    });

    const hasAccess = useCheckLaunchpadAccess({ launchpad: data });

    const { isActive, isNotActive, label } = useLaunchpadStatus({ launchpad: data });

    useEffect(() => {
        fetchUsers({
            userIds: [data.ownerId],
        });
    }, [data.ownerId, fetchUsers]);

    return (
        <ProtectedLink href={link} prefetch={false} disabled={!hasAccess}>
            <a rel="canonical" data-test-id={testId}>
                <StyledWrapper data-test-id="launchpad-card">
                    <StyledImgWrapper>
                        <LazyLoadMedia
                            ratio={44}
                            src={bannersAssets.src}
                            retinaSrc={bannersAssets.retina}
                            videoSrc={bannersAssets.videoSrc}
                            retinaVideoSrc={bannersAssets.retinaVideoSrc}
                        />
                    </StyledImgWrapper>
                    <StyledInfo>
                        <StyledImgPreviewWrapper>
                            <LazyLoadMedia
                                width={76}
                                height={76}
                                src={avatarAssets.src}
                                retinaSrc={avatarAssets.retina}
                                videoSrc={avatarAssets.videoSrc}
                                retinaVideoSrc={avatarAssets.retinaVideoSrc}
                            />
                        </StyledImgPreviewWrapper>
                        {creatorName && (
                            <Link href={creatorLink} prefetch={false}>
                                <StyledCreatorName rel="canonical">
                                    <Typography
                                        tag="div"
                                        overflowLines={1}
                                        altTitle={decodeHTMLEntities(creatorName)}
                                        size="xs"
                                    >
                                        {decodeHTMLEntities(creatorName)}
                                    </Typography>
                                </StyledCreatorName>
                            </Link>
                        )}
                        <StyledName justifyContent="center">
                            <Typography
                                altTitle={decodeHTMLEntities(data.name)}
                                size="sm"
                                variant="title"
                                overflowLines={1}
                                medium
                            >
                                {decodeHTMLEntities(data.name)}
                            </Typography>
                        </StyledName>
                        <StyledDescription size="sm" tag="div" overflowLines={2}>
                            {wipeHtml(data.description)}
                        </StyledDescription>
                        <StyledStatistics>
                            <StyledStatisticItem>
                                <Typography size="xs" medium color="white">
                                    {data.totalItems || 0}
                                </Typography>
                                <Typography size="xs">Total items</Typography>
                            </StyledStatisticItem>
                            <StyledStatisticItem>
                                <Typography size="xs" medium color="white">
                                    {makePriceByNano(+data.nanoPrice)}
                                </Typography>
                                <Typography size="xs">Price, {coins[data?.blockchainId]?.[0].symbol}</Typography>
                            </StyledStatisticItem>
                            <StyledStatisticItem>
                                <Typography medium color={isActive || isNotActive ? 'pink500' : 'white'} size="xs">
                                    {label}
                                </Typography>
                                <Typography size="xs">Mint date</Typography>
                            </StyledStatisticItem>
                        </StyledStatistics>
                    </StyledInfo>
                </StyledWrapper>
            </a>
        </ProtectedLink>
    );
};
